import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Select,
  Table,
  Button,
  Descriptions,
  Tag,
  Space,
  Row,
  Col,
  Typography,
  Empty,
  Modal,
  Divider,
  Tooltip,
} from 'antd';
import {
  SafetyOutlined,
  UserOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  PlusCircleOutlined,
  UnorderedListOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { formatMontant, formatDate, libelleTypeExpertise } from '../../variables/constants';
import PageHeader from '../../components/PageHeader';
import { useTauxRetenue, pourcent } from '../../services/tauxRetenueService';
import { useTranslation } from 'react-i18next';
import { localeIntl } from '../../i18n/format';

import { nonSelectionnable, basculer, clicANePasBasculer, partagerParPaiement } from './selectionLignes';

const { Option } = Select;

const round2 = (n) => Math.round(n * 100) / 100;

const extractList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.content)) return data.content;
  return [];
};

//  Composant Facture récapitulatif 
function FactureRecap({ expert, lignesSelectionnees, lignes, sinistres, recap, tauxTva, tauxRetenueValeur, tauxRetenue }) {
  const { t } = useTranslation('reglementsExperts');
  const today = new Date().toLocaleDateString(localeIntl(), { day: '2-digit', month: 'long', year: 'numeric' });
  const netVerse = round2(recap.totalTtc - recap.totalTaxe);

  return (
    <div
      style={{
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: 10,
        overflow: 'hidden',
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {/* En-tête facture */}
      <div
        style={{
          background: 'linear-gradient(135deg, #1a6b3c 0%, #28804e 100%)',
          padding: '20px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          color: '#fff',
        }}
      >
        <div>
          <div style={{ fontSize: 11, opacity: 0.75, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
            {t('multiple.recap.note')}
          </div>
          <div style={{ fontSize: 20, fontWeight: 800 }}>BNCB - CB Connect</div>
          <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>{t('multiple.recap.bureau')}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 2 }}>{t('multiple.recap.date')}</div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{today}</div>
          <div style={{ marginTop: 8 }}>
            <Tag style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', borderRadius: 12, fontSize: 11 }}>
              {t('multiple.recap.brouillon')}
            </Tag>
          </div>
        </div>
      </div>

      {/* Bénéficiaire */}
      <div style={{ padding: '16px 24px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{t('multiple.recap.beneficiaire')}</div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>{expert?.nomComplet ?? '-'}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>{expert?.typeExpert ?? ''}</div>
            {/* `paysLibelle`, et non `pays.libelle` : `ExpertResponse` est plat et n'a
                jamais porte d'objet `pays`. La ligne du pays ne s'affichait donc
                jamais sur le recapitulatif. */}
            {expert?.paysLibelle && (
              <div style={{ fontSize: 12, color: '#6b7280' }}>{expert.paysLibelle}</div>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{t('multiple.recap.conditions')}</div>
            <div style={{ fontSize: 12 }}>
              {t('multiple.recap.tva')} <strong>{(tauxTva * 100).toFixed(0)} %</strong>
            </div>
            <div style={{ fontSize: 12 }}>
              {t('multiple.recap.retenueSource')} <strong>{tauxRetenueValeur != null ? pourcent(tauxRetenueValeur) : '-'}</strong>
            </div>
            <div style={{ fontSize: 12 }}>
              {t('multiple.recap.reglements')} <strong>{lignesSelectionnees.length}</strong>
            </div>
          </div>
        </div>
      </div>

      {/* Tableau des lignes */}
      <div style={{ padding: '0 24px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f4f6f5' }}>
              <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #e5e7eb' }}>
                {t('multiple.recap.numeroSinistre')}
              </th>
              <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #e5e7eb' }}>
                {t('multiple.recap.libelle')}
              </th>
              <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, color: '#374151', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #e5e7eb' }}>
                {t('multiple.recap.ht')}
              </th>
              <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, color: '#374151', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #e5e7eb' }}>
                {t('multiple.recap.tvaColonne')}
              </th>
              <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, color: '#374151', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #e5e7eb' }}>
                {t('multiple.recap.ttc')}
              </th>
              <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, color: '#374151', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #e5e7eb' }}>
                {t('multiple.recap.retenue')}
              </th>
            </tr>
          </thead>
          <tbody>
            {lignesSelectionnees.map((l, idx) => {
              const sinistre = sinistres.find(s => s.sinistreTrackingId === l.sinistreTrackingId);
              const ht = parseFloat(l.montantHt) || 0;
              const tva = round2(ht * tauxTva);
              const ttc = round2(ht + tva);
              const taxe = round2(ttc * tauxRetenueValeur);
              return (
                <tr key={l.sinistreTrackingId} style={{ background: idx % 2 === 0 ? '#fff' : '#f9fafb' }}>
                  <td style={{ padding: '9px 8px', fontFamily: 'var(--font-main)', fontWeight: 600, color: '#1a6b3c', borderBottom: '1px solid #f3f4f6', fontSize: 12 }}>
                    {sinistre?.numeroSinistre ?? l.sinistreTrackingId?.slice(0, 8)}
                  </td>
                  <td style={{ padding: '9px 8px', color: '#374151', borderBottom: '1px solid #f3f4f6', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {sinistre?.sinistreLibelle ?? '-'}
                  </td>
                  <td style={{ padding: '9px 8px', textAlign: 'right', borderBottom: '1px solid #f3f4f6', fontFamily: 'var(--font-main)' }}>
                    {formatMontant(ht)}
                  </td>
                  <td style={{ padding: '9px 8px', textAlign: 'right', borderBottom: '1px solid #f3f4f6', fontFamily: 'var(--font-main)', color: '#9b563a' }}>
                    {formatMontant(tva)}
                  </td>
                  <td style={{ padding: '9px 8px', textAlign: 'right', borderBottom: '1px solid #f3f4f6', fontFamily: 'var(--font-main)', fontWeight: 600 }}>
                    {formatMontant(ttc)}
                  </td>
                  <td style={{ padding: '9px 8px', textAlign: 'right', borderBottom: '1px solid #f3f4f6', fontFamily: 'var(--font-main)', color: '#c4b935' }}>
                    -{formatMontant(taxe)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Totaux */}
      <div style={{ padding: '16px 24px 20px', display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ width: 280 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13 }}>
            <span style={{ color: '#6b7280' }}>{t('multiple.recap.sousTotalHt')}</span>
            <span style={{ fontFamily: 'var(--font-main)' }}>{formatMontant(recap.totalHt)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13 }}>
            <span style={{ color: '#9b563a' }}>{t('multiple.recap.tvaTaux', { taux: (tauxTva * 100).toFixed(0) })}</span>
            <span style={{ fontFamily: 'var(--font-main)', color: '#9b563a' }}>+{formatMontant(recap.totalTva)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13, borderTop: '1px solid #e5e7eb', marginTop: 4, paddingTop: 8 }}>
            <span style={{ fontWeight: 600 }}>{t('multiple.recap.totalTtc')}</span>
            <span style={{ fontFamily: 'var(--font-main)', fontWeight: 700 }}>{formatMontant(recap.totalTtc)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13 }}>
            <span style={{ color: '#c4b935' }}>{t('multiple.recap.retenueTaux', { taux: pourcent(tauxRetenueValeur) })}</span>
            <span style={{ fontFamily: 'var(--font-main)', color: '#c4b935' }}>-{formatMontant(recap.totalTaxe)}</span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '10px 14px',
              background: '#1a6b3c',
              borderRadius: 8,
              marginTop: 8,
            }}
          >
            <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{t('multiple.recap.netAVerser')}</span>
            <span style={{ color: '#fff', fontFamily: 'var(--font-main)', fontWeight: 800, fontSize: 16 }}>
              {formatMontant(netVerse)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

//  Composant principal 
export default function ReglementMultipleExperts() {
  const navigate = useNavigate();

  const { t } = useTranslation('reglementsExperts');

  const [expertId, setExpertId] = useState(null);
  // Taux de retenue : référentiel paramétrable. Pas de valeur de départ figée —
  // « 5 % » pouvait avoir été désactivé ; on prend le taux de l'expert choisi.
  const referentielTaux = useTauxRetenue();
  const [tauxRetenue, setTauxRetenue] = useState(null);
  const [lignes, setLignes] = useState({});
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  /* LE SINISTRE DONT ON REGARDE LE DETAIL. Le tableau ne porte plus que les quatre
     colonnes qui servent a decider - numero, date, fonds, montant. Tout le reste est
     ici, a la demande, plutot qu'en huit colonnes qu'il fallait faire defiler. */
  const [detail, setDetail] = useState(null);
  /* QUELLE VUE DU TABLEAU. « payables » par defaut : ce sont les lignes a traiter. Les
     deja payees se consultent a la demande - elles etaient auparavant melees aux autres,
     grisees et incochables, et sur un expert de longue date elles finissaient par etre
     la majorite du tableau. */
  const [vue, setVue] = useState('payables');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data: expertsData, isLoading: expertsLoading } = useQuery({
    queryKey: ['experts'],
    queryFn: () => axiosInstance.get('/v1/experts'),
  });

  const experts = extractList(expertsData?.data);
  const expertSelectionne = experts.find((e) => e.expertTrackingId === expertId) || null;

  const { data: sinistresData, isLoading: sinistresLoading } = useQuery({
    queryKey: ['sinistres-payables', expertId],
    queryFn: () =>
      axiosInstance.get('/v1/lot-reglements/sinistres-payables', {
        params: { expertTrackingId: expertId },
      }),
    enabled: !!expertId,
  });

  const sinistres = extractList(sinistresData?.data);

  useMemo(() => {
    if (!sinistres.length) return;
    const init = {};
    sinistres.forEach((s) => {
      init[s.sinistreTrackingId] = {
        sinistreTrackingId: s.sinistreTrackingId,
        montantHt: s.montantHtPropose ?? 0,
        dejaPaye: s.dejaPaye,
      };
    });
    setLignes(init);
    setSelectedRowKeys([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sinistresData]);

  // TVA non appliquée pour les règlements aux experts (décision métier).
  const tauxTva = 0;
  // null tant qu'aucun taux en vigueur n'est choisi : la création reste alors
  // bloquée, plutôt que de calculer une retenue sur un taux supposé.
  const tauxRetenueValeur = referentielTaux.options.find((o) => o.value === tauxRetenue)?.valeur ?? null;

  const recap = useMemo(() => {
    let totalHt = 0;
    let totalTva = 0;
    let totalTtc = 0;
    let totalTaxe = 0;

    selectedRowKeys.forEach((key) => {
      const l = lignes[key];
      if (!l || l.dejaPaye) return;
      const ht = parseFloat(l.montantHt) || 0;
      const tva = round2(ht * tauxTva);
      const ttc = round2(ht + tva);
      const taxe = round2(ttc * tauxRetenueValeur);
      totalHt += ht;
      totalTva += tva;
      totalTtc += ttc;
      totalTaxe += taxe;
    });

    return {
      totalHt: round2(totalHt),
      totalTva: round2(totalTva),
      totalTtc: round2(totalTtc),
      totalTaxe: round2(totalTaxe),
    };
  }, [selectedRowKeys, lignes, tauxTva, tauxRetenueValeur]);

  const mutation = useMutation({
    mutationFn: (body) => axiosInstance.post('/v1/lot-reglements', body),
    onSuccess: (res) => {
      const lotTrackingId =
        res?.data?.data?.lotTrackingId ?? res?.data?.lotTrackingId;
      toast.success(t('multiple.messages.lotCree'));
      navigate('/lot-reglements/' + lotTrackingId);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t('multiple.messages.erreurCreation'));
    },
  });

  const handleExpertChange = (id) => {
    setExpertId(id);
    setLignes({});
    setDetail(null);
    setVue('payables');
    setSelectedRowKeys([]);
    const exp = experts.find((x) => x.expertTrackingId === id);
    const enVigueur = referentielTaux.options.some((o) => o.value === exp?.tauxRetenue);
    setTauxRetenue(enVigueur ? exp.tauxRetenue : null);
  };

  const handleConfirmCreate = () => {
    const lignesSelectionnees = selectedRowKeys
      .map((key) => lignes[key])
      .filter((l) => l && !l.dejaPaye);

    const body = {
      expertTrackingId: expertId,
      tauxRetenue,
      lignes: lignesSelectionnees.map((l) => ({
        sinistreTrackingId: l.sinistreTrackingId,
        montantHt: parseFloat(l.montantHt) || 0,
      })),
    };
    mutation.mutate(body);
    setConfirmOpen(false);
  };

  const lignesSelectionnees = selectedRowKeys
    .map((key) => lignes[key])
    .filter((l) => l && !l.dejaPaye);

  const canSubmit = !!expertId && lignesSelectionnees.length > 0 && tauxRetenueValeur != null
    && !mutation.isPending;

  const { payables, payes } = partagerParPaiement(sinistres);
  const sinistresAffiches = vue === 'payes' ? payes : payables;
  const enVuePayes = vue === 'payes';

  /* LE MONTANT TTC D'UNE LIGNE, calcule au meme endroit par la colonne des fonds et
     par la regle de selection. Il l'etait deux fois, a l'identique. */
  const ttcDeLaLigne = (record) => {
    const ligne = lignes[record.sinistreTrackingId];
    const ht = Number(ligne?.montantHt ?? record.montantHtPropose ?? 0);
    return ht * (1 + tauxTva);
  };

  /* QUATRE COLONNES, ET NON HUIT. Le tableau servait a la fois a decider et a tout
     montrer : huit colonnes qu'il fallait faire defiler horizontalement pour cocher
     une ligne. Ne restent que les quatre informations sur lesquelles la decision se
     prend - le numero, la date de survenance, les fonds disponibles et le montant.

     Le reste n'est pas perdu : il s'ouvre dans un tiroir, a la demande. Le statut non
     plus : la ligne sous les fonds disponibles dit desormais les trois cas - deja paye,
     fonds suffisants, ou ce qui manque. C'etait une colonne a part, qui redisait ce que
     cette ligne calculait deja. */
  const columns = [
    {
      title: t('multiple.colonnes.numeroSinistre'),
      dataIndex: 'numeroSinistre',
      key: 'numeroSinistre',
      width: 140,
      render: (v) => (
        <span style={{ fontFamily: 'var(--font-main)', fontWeight: 600, color: '#1a6b3c' }}>{v}</span>
      ),
    },
    {
      // La date de survenance du sinistre, pas celle du rapport d'expertise.
      title: t('multiple.colonnes.survenuLe'),
      dataIndex: 'dateSinistre',
      key: 'dateSinistre',
      width: 110,
      render: (v) => formatDate(v),
    },
    {
      title: t('multiple.colonnes.fondsDisponible'),
      key: 'fondsDispo',
      width: 190,
      align: 'right',
      render: (_, record) => {
        const dispo = Number(record.fondsDisponibles ?? 0);
        const ttc = ttcDeLaLigne(record);
        const suffisant = dispo >= ttc;
        return (
          <div>
            <div style={{
              fontFamily: 'var(--font-main)',
              fontWeight: 600,
              color: record.dejaPaye ? '#9ca3af' : (suffisant ? '#1a6b3c' : '#dc2626'),
            }}>
              {formatMontant(dispo)}
            </div>
            {/* LA LIGNE QUI REMPLACE LA COLONNE « STATUT ». Les trois cas se disent ici,
                la ou se trouve le chiffre qui les explique. */}
            <div style={{ fontSize: 11 }}>
              {record.dejaPaye
                ? <span style={{ color: '#9ca3af' }}>{t('multiple.colonnes.dejaPaye')}</span>
                : (suffisant
                  ? <span style={{ color: '#1a6b3c' }}>{t('multiple.colonnes.suffisant', { montant: formatMontant(ttc) })}</span>
                  : <span style={{ color: '#dc2626' }}>{t('multiple.colonnes.manque', { montant: formatMontant(ttc - dispo) })}</span>)}
            </div>
          </div>
        );
      },
    },
    {
      title: t('multiple.colonnes.montantHt'),
      dataIndex: 'sinistreTrackingId',
      key: 'montantHt',
      width: 140,
      align: 'right',
      render: (id, record) => {
        const ligne = lignes[id];
        const montant = ligne?.montantHt ?? record.montantHtPropose ?? 0;
        return (
          <span
            title={t('multiple.colonnes.montantFixe')}
            style={{ fontFamily: 'var(--font-main)', fontWeight: 600 }}
          >
            {formatMontant(montant)}
          </span>
        );
      },
    },
    {
      title: '',
      key: 'detail',
      width: 48,
      align: 'center',
      render: (_, record) => (
        <Tooltip title={t('multiple.colonnes.detailsSinistre')}>
          {/* `data-sans-bascule` : ce bouton fait son propre travail. Sans cette marque,
              l'ouvrir cocherait le sinistre au passage, la ligne entiere etant
              cliquable. */}
          <Button
            type="text"
            size="small"
            data-sans-bascule
            icon={<InfoCircleOutlined />}
            onClick={() => setDetail(record)}
            style={{ color: '#6b7280' }}
          />
        </Tooltip>
      ),
    },
  ];

  /* LA MEME REGLE POUR LA CASE ET POUR LA LIGNE. Si le clic de ligne portait sa propre
     condition, les deux auraient fini par differer, et l'on aurait pu retenir en
     cliquant la ligne un sinistre que la case refusait. */
  const indisponible = (record) => nonSelectionnable(record, lignes, tauxTva);

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (record) => ({ disabled: indisponible(record) }),
  };

  /* LE CLIC SUR TOUTE LA LIGNE. La case a cocher etait la seule cible : quinze pixels
     dans un tableau qui en fait mille de large.

     Le clic venu de la colonne de selection est laisse a la case : la case etant DANS
     la ligne, il declencherait sinon les deux - deux bascules, donc aucun effet, et une
     case qui refuse de se cocher. */
  const clicLigne = (record) => (event) => {
    if (clicANePasBasculer(event.target)) return;
    if (indisponible(record)) return;
    setSelectedRowKeys((cles) => basculer(cles, record.sinistreTrackingId));
  };

  return (
    <Layout title={t('multiple.titre')}>
      {/* Le compte n'a de sens qu'une fois un expert choisi : sans expertId la
          requete n'est meme pas lancee, et pendant son chargement `sinistres` est
          vide - afficher « 0 sinistre » ferait croire a un expert sans dossier. */}
      <PageHeader
        titre={t('multiple.titre')}
        icone={<SafetyOutlined />}
        compte={expertId && !sinistresLoading ? payables.length : undefined}
        unite={t('multiple.unite')}
        unitePluriel={t('multiple.unitePluriel')}
        actions={(
          <Button
            icon={<UnorderedListOutlined />}
            onClick={() => navigate('/lot-reglements')}
            style={{ borderRadius: 8 }}
          >
            {t('multiple.voirLots')}
          </Button>
        )}
      />

      {/* UNE SEULE CARD POUR CHOISIR ET POUR RETENIR.

          L'ecran en portait deux, empilees : il fallait descendre pour voir les
          sinistres, et remonter pour relire le tarif de l'expert. Les deux gestes
          appartiennent a la meme decision, et tiennent maintenant cote a cote -
          l'expert et ses informations a gauche, ses sinistres payables a droite.

          La card de la facture reste a part : elle n'apparait qu'une fois la selection
          faite, et c'est le resultat, non la saisie. */}
      <div className="cb-card" style={{ marginBottom: 16 }}>
        <Row gutter={[24, 16]}>
          {/*  GAUCHE : l'expert, puis ses informations  */}
          <Col xs={24} md={8}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <UserOutlined style={{ color: '#1a6b3c', fontSize: 16 }} />
              <span className="cb-card-title">{t('multiple.expert')}</span>
            </div>

            <Select
              showSearch
              placeholder={t('multiple.choisirExpert')}
              loading={expertsLoading}
              value={expertId}
              onChange={handleExpertChange}
              optionFilterProp="children"
              style={{ width: '100%', borderRadius: 8 }}
              filterOption={(input, option) =>
                (option?.children ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {experts.map((e) => (
                <Option key={e.expertTrackingId} value={e.expertTrackingId}>
                  {e.nomComplet} ({e.typeExpert})
                </Option>
              ))}
            </Select>

            {/* EN DESSOUS, ses informations. `column={1}` et non quatre : la colonne est
                etroite, et une grille de quatre y aurait tronque chaque libelle. */}
            {expertSelectionne && (
              <Descriptions size="small" column={1} bordered style={{ marginTop: 16 }}>
                <Descriptions.Item label={t('multiple.type')}>
                  {expertSelectionne.typeExpert === 'MEDICAL' ? t('multiple.typesExpert.MEDICAL') : t('multiple.typesExpert.AUTOMOBILE')}
                </Descriptions.Item>
                <Descriptions.Item label={t('multiple.pays')}>
                  {expertSelectionne.paysLibelle ?? '-'}
                </Descriptions.Item>
                {/* PAS DE LIGNE TVA. La TVA ne s'applique pas aux reglements aux experts
                    (`tauxTva = 0` plus bas) : afficher le taux du pays a cote d'une
                    facture calculee a 0 % se contredisait a l'ecran. */}
                <Descriptions.Item label={t('multiple.tarifDefaut')}>
                  {formatMontant(expertSelectionne.montExpertise)}
                </Descriptions.Item>
                <Descriptions.Item label={t('multiple.tauxRetenueDefaut')}>
                  {referentielTaux.affichage(expertSelectionne.tauxRetenue)}
                </Descriptions.Item>
                {expertSelectionne.nif && (
                  <Descriptions.Item label={t('multiple.nif')}>{expertSelectionne.nif}</Descriptions.Item>
                )}
              </Descriptions>
            )}
          </Col>

          {/*  DROITE : les sinistres payables  */}
          <Col xs={24} md={16}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 12, flexWrap: 'wrap', marginBottom: 14,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileSearchOutlined style={{ color: '#1a6b3c', fontSize: 16 }} />
                <span className="cb-card-title">
                  {enVuePayes ? t('multiple.sinistresDejaPayes') : t('multiple.sinistresPayables')}
                </span>
                {lignesSelectionnees.length > 0 && (
                  <Tag color="green" style={{ borderRadius: 12, marginLeft: 4 }}>
                    {t('multiple.selectionnes', { count: lignesSelectionnees.length })}
                  </Tag>
                )}
              </div>
              {expertId && (
                <Space>
                  {/* LA BASCULE ENTRE LES DEUX VUES. Les deja payes ne se cochent pas :
                      on les consulte, et le compte dit tout de suite s'il y en a. */}
                  <Button
                    size="small"
                    icon={enVuePayes ? <UnorderedListOutlined /> : <HistoryOutlined />}
                    onClick={() => setVue(enVuePayes ? 'payables' : 'payes')}
                    style={{ borderRadius: 8 }}
                  >
                    {enVuePayes
                      ? t('multiple.vuePayables', { n: payables.length })
                      : t('multiple.vuePayes', { n: payes.length })}
                  </Button>
                  {/* LE TAUX NE SERT QU'A CE QU'ON VA CREER. En vue « deja payes », il
                      n'y a rien a creer, et son avertissement orange n'aurait aucun sens. */}
                  {!enVuePayes && (
                    <>
                      <Typography.Text style={{ color: '#6b7280', fontSize: 13 }}>{t('multiple.tauxRetenue')}</Typography.Text>
                      <Select
                        value={tauxRetenue}
                        onChange={setTauxRetenue}
                        placeholder={t('multiple.choisir')}
                        status={tauxRetenue ? undefined : 'warning'}
                        style={{ minWidth: 130, borderRadius: 8 }}
                        popupMatchSelectWidth={false}
                        options={referentielTaux.options}
                      />
                    </>
                  )}
                </Space>
              )}
            </div>

            {expertId ? (
              <>
                <Table
                  className="cb-table"
                  rowKey="sinistreTrackingId"
                  columns={columns}
                  dataSource={sinistresAffiches}
                  loading={sinistresLoading}
                  /* PAS DE CASES A COCHER SUR LES DEJA PAYES. Elles seraient toutes
                     desactivees : une colonne de cases grises qui ne repond a aucun clic
                     se lit comme une panne, pas comme une interdiction. */
                  rowSelection={enVuePayes ? undefined : rowSelection}
                  pagination={false}
                  size="small"
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                          <span style={{ color: '#6b7280' }}>
                            {enVuePayes
                              ? t('multiple.aucunPaye')
                              : t('multiple.aucunSinistre')}
                          </span>
                        }
                      />
                    ),
                  }}
                  scroll={{ x: 'max-content' }}
                  rowClassName={(record) => (record.dejaPaye ? 'row-deja-paye' : '')}
                  onRow={(record) => ({
                    // En vue « deja payes », la ligne n'ouvre que le popup des details :
                    // il n'y a rien a cocher.
                    onClick: enVuePayes ? undefined : clicLigne(record),
                    // Le curseur dit ce qui est cliquable : une ligne indisponible ne doit
                    // pas paraitre l'etre, sans quoi le clic sans effet passe pour une panne.
                    style: (enVuePayes || indisponible(record)) ? undefined : { cursor: 'pointer' },
                  })}
                />
                <style>{`
                  .row-deja-paye td { background: #f4f6f5 !important; color: #9ca3af; }
                `}</style>
              </>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span style={{ color: '#6b7280' }}>
                    {t('multiple.choisirExpertAide')}
                  </span>
                }
                style={{ padding: '32px 0' }}
              />
            )}
          </Col>
        </Row>
      </div>

      {/* Récapitulatif format facture */}
      {expertId && lignesSelectionnees.length > 0 && (
        <div className="cb-card" style={{ marginBottom: 20 }}>
          <div className="cb-card-header" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FileTextOutlined style={{ color: '#1a6b3c', fontSize: 16 }} />
              <span className="cb-card-title">
                {t('multiple.recapitulatif', { count: lignesSelectionnees.length })}
              </span>
            </div>
          </div>
          <FactureRecap
            expert={expertSelectionne}
            lignesSelectionnees={lignesSelectionnees}
            lignes={lignes}
            sinistres={sinistres}
            recap={recap}
            tauxTva={tauxTva}
            tauxRetenueValeur={tauxRetenueValeur}
            tauxRetenue={tauxRetenue}
          />
        </div>
      )}

      {/* Bouton créer */}
      <Row justify="end">
        <Button
          type="primary"
          icon={<PlusCircleOutlined />}
          disabled={!canSubmit}
          loading={mutation.isPending}
          onClick={() => setConfirmOpen(true)}
          style={{
            background: '#1a6b3c',
            borderColor: '#1a6b3c',
            borderRadius: 8,
            fontWeight: 600,
          }}
        >
          {t('multiple.creerLot')}
        </Button>
      </Row>

      {/* Modal de confirmation */}
      <Modal
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        footer={null}
        width={560}
        destroyOnClose
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ExclamationCircleOutlined style={{ color: '#1a6b3c', fontSize: 18 }} />
            <span style={{ fontWeight: 700 }}>{t('multiple.confirmation.titre')}</span>
          </div>
        }
      >
        {/* Résumé dans la modale */}
        <div
          style={{
            background: '#f0faf4',
            border: '1px solid #a7f3d0',
            borderRadius: 10,
            padding: '16px 20px',
            marginBottom: 20,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ color: '#6b7280', fontSize: 13 }}>{t('multiple.expert')}</span>
            <strong style={{ color: '#111827' }}>{expertSelectionne?.nomComplet}</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ color: '#6b7280', fontSize: 13 }}>{t('multiple.confirmation.nombreSinistres')}</span>
            <strong style={{ color: '#111827' }}>{lignesSelectionnees.length}</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ color: '#6b7280', fontSize: 13 }}>{t('multiple.recap.totalTtc')}</span>
            <strong style={{ color: '#1a6b3c', fontFamily: 'var(--font-main)' }}>{formatMontant(recap.totalTtc)}</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ color: '#6b7280', fontSize: 13 }}>{t('multiple.confirmation.retenueSource')}</span>
            <strong style={{ color: '#c4b935', fontFamily: 'var(--font-main)' }}>-{formatMontant(recap.totalTaxe)}</strong>
          </div>
          <Divider style={{ margin: '10px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 700, fontSize: 15 }}>{t('multiple.recap.netAVerser')}</span>
            <strong style={{ color: '#1a6b3c', fontFamily: 'var(--font-main)', fontSize: 17 }}>
              {formatMontant(round2(recap.totalTtc - recap.totalTaxe))}
            </strong>
          </div>
        </div>

        <Typography.Text style={{ color: '#6b7280', fontSize: 13, display: 'block', marginBottom: 20 }}>
          {t('multiple.confirmation.texte')}
        </Typography.Text>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button style={{ borderRadius: 8 }} onClick={() => setConfirmOpen(false)}>
            {t('actions.annuler')}
          </Button>
          <Button
            type="primary"
            icon={<PlusCircleOutlined />}
            loading={mutation.isPending}
            onClick={handleConfirmCreate}
            style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8, fontWeight: 600 }}
          >
            {t('multiple.confirmation.confirmer')}
          </Button>
        </div>
      </Modal>
      {/* LES DETAILS DU SINISTRE, DANS UN POPUP.

          Le tableau ne porte plus que de quoi decider. Tout ce qu'il montrait en plus -
          le libelle, la police, le type d'expertise, la date du rapport, et ce qui a ete
          encaisse puis deja regle - s'ouvre ici, pour un sinistre a la fois.

          Une fenetre centree plutot qu'un tiroir : le tableau occupe la droite de
          l'ecran et l'expert la gauche, de sorte qu'un tiroir recouvrait toujours l'un
          des deux. Le popup se ferme d'un clic a cote, et rend la vue entiere. */}
      <Modal
        open={!!detail}
        onCancel={() => setDetail(null)}
        footer={null}
        width={520}
        destroyOnClose
        title={detail
          ? t('multiple.detailSinistre.titre', { numero: detail.numeroSinistre ?? '' }).trim()
          : t('multiple.detailSinistre.titreSansNumero')}
      >
        {detail && (
          <>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label={t('multiple.detailSinistre.libelle')}>
                {detail.sinistreLibelle || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('multiple.detailSinistre.numeroPolice')}>
                {detail.numeroPolice || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('multiple.detailSinistre.dateSinistre')}>
                {formatDate(detail.dateSinistre)}
              </Descriptions.Item>
              <Descriptions.Item label={t('multiple.detailSinistre.typeExpertise')}>
                {detail.typeExpertise ? libelleTypeExpertise(detail.typeExpertise) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('multiple.detailSinistre.dateRapport')}>
                {formatDate(detail.dateRapport)}
              </Descriptions.Item>
            </Descriptions>

            <Divider style={{ margin: '20px 0 12px' }} />
            <Typography.Text strong style={{ color: '#374151' }}>
              {t('multiple.detailSinistre.fonds')}
            </Typography.Text>

            {/* CES TROIS CHIFFRES SE LISENT ENSEMBLE : l'encaisse, moins ce qui a deja
                ete regle, donne le disponible. C'est le calcul que le serveur refait
                avant d'accepter le lot, et la raison pour laquelle une ligne peut etre
                refusee. */}
            <Descriptions size="small" column={1} bordered style={{ marginTop: 10 }}>
              <Descriptions.Item label={t('multiple.detailSinistre.totalEncaisse')}>
                {formatMontant(detail.totalEncaisse)}
              </Descriptions.Item>
              <Descriptions.Item label={t('multiple.detailSinistre.dejaRegle')}>
                {formatMontant(detail.totalDejaPaye)}
              </Descriptions.Item>
              <Descriptions.Item label={t('multiple.detailSinistre.fondsDisponibles')}>
                <span style={{ fontWeight: 700 }}>{formatMontant(detail.fondsDisponibles)}</span>
              </Descriptions.Item>
              <Descriptions.Item label={t('multiple.detailSinistre.montantHtPropose')}>
                {formatMontant(lignes[detail.sinistreTrackingId]?.montantHt
                  ?? detail.montantHtPropose)}
              </Descriptions.Item>
            </Descriptions>

            {detail.dejaPaye && (
              <Tag color="error" style={{ marginTop: 16 }}>
                {t('multiple.detailSinistre.dejaRegleExpert')}
              </Tag>
            )}
            {!detail.dejaPaye
              && Number(detail.fondsDisponibles ?? 0) < ttcDeLaLigne(detail) && (
                <Tag color="warning" style={{ marginTop: 16, whiteSpace: 'normal', height: 'auto' }}>
                  {t('multiple.detailSinistre.fondsInsuffisants', {
                    montant: formatMontant(ttcDeLaLigne(detail) - Number(detail.fondsDisponibles ?? 0)),
                  })}
                </Tag>
              )}
          </>
        )}
      </Modal>

    </Layout>
  );
}
